import { useAPDs, useExportTrackerDataApd } from "@/api/trackerdata-apd";
import { nullElement } from "@/components/null-element";
import { TableDefault } from "@/components/table/table-default";
import { TableForm } from "@/components/table/table-form";
import {
  DataConfig,
  useDefaultDataConfig,
} from "@/components/table/useDefaultDataConfig";
import { ROLE } from "@/utils/constant";
import { generateCustomSpec } from "@/utils/helper";
import { useForm } from "react-hook-form";
import { useCurrentUser } from "../auth/useCurrentUser";
import { swalErrorDefault } from "@/utils/lib/swal";
import { DownloadButton } from "@/components/ui/button.tsx";
import { useEffect, useState } from "react";
import { axiosInstance } from "@/utils/lib/axios";
import { Badge } from "@/components/ui/badge.tsx";
import { TableHeaderFilter } from "@/components/table/table-header-filter";
import { TableCell } from "@/components/table/table-cell";
import { getBadgeVariant } from "@/utils/helper/getBadgeVariant";
import { getExceedDuration } from "@/utils/helper/getExceedDuration";
import { useProfileByKeycloakId } from "@/api/auth";
import { useMonthTarget } from "@/api/master-param";

const HeaderElement = ({
  dataConfig,
  initStatus,
}: {
  dataConfig: DataConfig;
  initStatus: string | null;
}) => {
  const [status, setStatus] = useState<string>("IDLE");
  const { mutate: exportList, isPending: isDownloading } =
    useExportTrackerDataApd();
  const { id: keycloakId } = useCurrentUser();
  const { data } = useProfileByKeycloakId({ keycloakId: keycloakId });

  // Add more detailed logging
  const downloadTimestamp = localStorage.getItem("isDownloadApd");
  const isDownloadApd = Boolean(
    downloadTimestamp && downloadTimestamp.trim().length > 0,
  );
  const stat = initStatus && Boolean(isDownloadApd) ? initStatus : status;

  const handleExport = () => {
    const params = {
      ...dataConfig,
      entityId: data?.id,
      limit: undefined,
      page: undefined,
    };

    if (stat === "IDLE") {
      setStatus("IN PROGRESS");
    }

    exportList(
      {
        config: params,
        isDownload: isDownloadApd,
        onDownload: !!isDownloadApd,
      },
      {
        onSuccess: (response) => {
          setStatus(response.status);
        },
        onError: () => {
          swalErrorDefault.fire();
        },
      },
    );
  };

  return (
    <div className="flex items-center justify-end gap-2">
      <Badge variant={getBadgeVariant(stat)}>{stat}</Badge>&nbsp;
      <DownloadButton onClick={handleExport} loading={isDownloading} />
    </div>
  );
};

export function APDTable() {
  const { dataConfig, setDataConfig } = useDefaultDataConfig();
  const { roles } = useCurrentUser();
  const isVendor = roles?.includes(ROLE.VENDOR);
  const isSysAdminView = roles?.includes(ROLE.SYS_ADMIN_VIEW);
  const isValidator = roles?.includes(ROLE.VALIDATOR_DOCUMENT_OFFICER);
  const isVendorView = roles?.includes(ROLE.VENDOR_VIEW);

  const filterForm = useForm();
  const [status, setStatus] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false); // Add state to track processing

  const { data: apds } = useAPDs(dataConfig);
  const { mutate: exportList } = useExportTrackerDataApd();
  const { id: keycloakId } = useCurrentUser();
  const { data } = useProfileByKeycloakId({ keycloakId: keycloakId });

  const { data: itv_monthTarget } = useMonthTarget();
  const itvMonthTargetOptions = itv_monthTarget?.content
    .map((itvMonthTarget) => ({
      label: itvMonthTarget.value,
      key: itvMonthTarget.paramTag,
    }))
    .sort((a, b) => Number(a.key) - Number(b.key));

  const customSpec = generateCustomSpec({
    path: "/api/crud/trackerdata-apd",
    canBulk: false,
    fields: [
      {
        name: "cityTown",
        label: "City Town",
        searchable: true,
        sortable: true,
      },
      {
        name: "hpPermit",
        label: "HP Permit",
        searchable: true,
        sortable: true,
      },
      {
        name: "hpDesign",
        label: "HP Design",
        searchable: true,
        sortable: true,
      },
      {
        name: "suffixPermit",
        label: "Suffix Permit",
        searchable: true,
        sortable: true,
      },
      {
        name: "clusterId",
        label: "cluster ID",
        searchable: true,
        sortable: true,
      },
      {
        name: "projectName",
        label: "Project Name",
        searchable: true,
        sortable: true,
      },
      {
        name: "fdtId",
        label: "FDTID",
        searchable: true,
        sortable: true,
      },
      {
        name: "drawingIdApd",
        label: "Drawing ID APD",
        searchable: true,
        sortable: true,
      },
      {
        name: "oltType",
        label: "OLT Type",
        searchable: true,
        sortable: true,
        select_options: {
          options: [
            { label: "OLT Centralized", key: "CENTRALIZED" },
            { label: "OLT Decentralized", key: "DECENTRALIZED" },
          ],
        },
      },
      {
        name: "oltIdaction",
        label: "OLT-ID",
        searchable: true,
        sortable: true,
      },
      {
        name: "combineStatus",
        label: "Combined Status",
        searchable: true,
        sortable: true,
      },
      {
        name: "latitude",
        label: "Latitude",
        searchable: true,
        sortable: true,
      },
      {
        name: "longitude",
        label: "Longitude",
        searchable: true,
        sortable: true,
      },
      {
        name: "profileName",
        label: "Profile Name",
        searchable: true,
        sortable: true,
      },
      {
        name: "rollOutType",
        label: "Roll-Out Type",
        searchable: true,
        sortable: true,
      },
      {
        name: "tenantName",
        label: "Tenant Name",
        searchable: true,
        sortable: true,
      },
      {
        name: "vendor",
        label: "Vendor",
        searchable: true,
        sortable: true,
      },
      {
        name: "monthTargetString",
        label: "Month Target",
        searchable: true,
        sortable: true,
        select_options: {
          options: itvMonthTargetOptions,
        },
      },
      {
        name: "yearTarget",
        label: "Year Target",
        searchable: true,
        sortable: true,
      },
      {
        name: "createdDate",
        label: "Created Date",
        searchable: true,
        sortable: true,
      },
      {
        name: "updatedDate",
        label: "Updated Date",
        searchable: true,
        sortable: true,
      },
      {
        name: "createdByName",
        label: "Created By",
        searchable: true,
        sortable: true,
      },
      {
        name: "updatedByName",
        label: "Updated By",
        searchable: true,
        sortable: true,
      },
    ],
  });

  useEffect(() => {
    const handleExport = async () => {
      const params = {
        ...dataConfig,
        entityId: data?.id,
        limit: undefined,
        page: undefined,
      };

      // Add more robust localStorage check
      const downloadTimestamp = localStorage.getItem("isDownloadApd");
      const isDownloadApd = Boolean(
        downloadTimestamp && downloadTimestamp.trim().length > 0,
      );

      if (isDownloadApd && !isProcessing) {
        setIsProcessing(true);
        try {
          // Fetch duration from API
          const response = await axiosInstance.get(
            "/api/crud/master-param?paramType=ITV_ASYNC_IDLE&active=true",
          );

          const exceedDuration = getExceedDuration(
            downloadTimestamp ?? "",
            response,
          );

          if (exceedDuration) {
            localStorage.removeItem("isDownloadApd");
            setStatus(null);
            setIsProcessing(false);
          } else {
            exportList(
              {
                config: params,
                isDownload: isDownloadApd,
                onDownload: false,
              },
              {
                onSuccess: (response) => {
                  setStatus(response.status);
                  setIsProcessing(false);
                },
                onError: () => {
                  swalErrorDefault.fire();
                  setIsProcessing(false);
                },
              },
            );
          }
        } catch (error) {
          console.error("Error fetching duration:", error);
          setIsProcessing(false);
        }
      }
    };

    handleExport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportList, isProcessing]);

  const disableCreate =
    isVendor || isValidator || isSysAdminView || isVendorView;
  const disableEdit = isVendor || isSysAdminView || isVendorView;

  return (
    <>
      <TableForm
        setDataConfig={setDataConfig}
        form={filterForm}
        createPath={disableCreate ? null : "/master-data/apd/create"}
        createLabel="Add APD"
        editPath={disableEdit ? null : "/master-data/apd/edit/bulk"}
        editLabel="Edit Bulk APD"
      />
      <TableDefault
        spec={customSpec}
        data={apds}
        dataConfig={dataConfig}
        setDataConfig={setDataConfig}
        headerElement={
          <HeaderElement dataConfig={dataConfig} initStatus={status} />
        }
        hideActionColumn={isVendor}
        customButtonDetail={
          isSysAdminView || isVendorView ? undefined : nullElement
        }
        customButtonEdit={
          isSysAdminView || isVendorView ? nullElement : undefined
        }
        searchFilterByColumns={true}
        customFilterByColumns={TableHeaderFilter}
        customCell={TableCell}
      />
    </>
  );
}
